import React, { useEffect, useState, useContext } from 'react'
import { useLoaderData, useParams } from 'react-router-dom'
import { useAuth } from '../../../../../hooks/auth'

import {
    Contract,
    ContractPosition,
    CurrencyRate,
    ItemDetail,
    LongTermContractVolumes,
    ContractSupplier,
} from '../../types'

import {
    CurrencyList,
    PlanPositionRow,
} from '../../../Plans/Positions/Edit/types'

import ContractStage from './components/ContractStage'
import ContractInfo from './components/ContractInfo'
import ContractPurchaseInfo from './components/ContractPurchaseInfo'
import ContractBaseInfo from './components/ContractBaseInfo'
import ContractChangeInfo from './components/ContractChangeInfo'

import Footer from './components/Footer'

import { Structure } from '../../../Org/Structure/types'
import {
    ContractEditErrors,
    ContractChangeInfoError,
    ContractPurchaseInfoError,
    ContractBaseInfoError,
    ContractConfirmingDocsError,
    ContractDocumentsError,
    ContractSuppliersInfoError,
    ContractPositionsInfoError,
    ContractPositionRowError,
    ContractSupplierRowError,
    RegionRf,
} from './types'

import { User } from '../../../Org/Users/types'
import { OrganizationInfo } from '../../../Org/Home/types'

import { ThemeContext } from '../../../../../components/ThemeContext'

import HoverTooltip from '../../../../../components/HoverTooltip'

import ContractPaymentBreakdown from './components/ContractPaymentBreakdown'

import ContractDocuments from './components/ContractDocuments'
import ContractConfirmingDocs from './components/ContractConfirmingDocs'
import ContractPositionInfo from './components/ContractPositionInfo'
import ContractSuppliers from './components/ContractSuppliers'
import { CountryList } from '../Performances/types'

import {
    createEmptyContractPosition,
    createContractPositionFromPlanRow,
    createContractPositionFromContract,
    createContractPositionFromPurchaseLotItem,
    getNextOrdinalNumber,
} from '../utils/ContractPositionFactory'
import ContractPlanPositionSelectPopup from './components/ContractPlanPositionSelectPopup'
import {
    Purchase,
    PurchaseLot,
    PurchaseLotItem,
} from '../../../Purchases/types'
import { PurchaseMethod } from '../../../Org/PurchaseMethods/types'
import ContractPurchaseSelectPopup from './components/ContractPurchaseSelectPopup'

type ApiResponseRoleGuid = {
    result: QueryItem[]
}

type QueryItem = {
    guid: string
    name: string
    organization_guid: string
    created_at: string
    updated_at: string
}

export const loadContractData = async ({
    params,
}: any): Promise<Contract | null> => {
    const action = params.action
    const contract_reg_number = params.contract_reg_number
    const contract_action = params.contract_action
    const contract_guid = params.contract_guid

    // Для create — ничего не грузим
    if (!contract_guid) return null

    const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/contract/guid/${contract_guid}`

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
        // пусть отрисуется ErrorPage
        throw new Response('Failed to fetch contract', {
            status: response.status,
        })
    }

    const data = await response.json()

    // API может вернуть либо объект, либо массив с одним объектом
    const contract: Contract | null = Array.isArray(data)
        ? (data[0] ?? null)
        : (data ?? null)

    // Доп. проверка, что это действительно контракт
    if (!contract || !('guid' in contract)) return null

    return contract
}

const roundTo = (value: number, digits = 4): number => {
    const factor = 10 ** digits
    return Math.round((value + Number.EPSILON) * factor) / factor
}

function getCurrentYear() {
    const today = new Date()
    return today.getFullYear() // Возвращает текущий год
}

function getCurrentMonth() {
    const today = new Date()
    return today.getMonth() // Возвращает номер месяца (0–11)
}

const getCurrentDate = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const formatDate = (isoString: string): string => {
    const date = new Date(isoString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${year}-${month}-${day}`
}

const toNumber = (value: unknown): number => {
    if (value === null || value === undefined || value === '') return 0
    if (typeof value === 'number') return value

    const parsed = Number(String(value).replace(/\s/g, '').replace(',', '.'))
    return Number.isNaN(parsed) ? 0 : parsed
}

const getStructureList = async (): Promise<Structure[]> => {
    return fetch(
        process.env.REACT_APP_API_URL + '/api/backend/v1/organization_struct',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на способы закупок')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getRole = async (role_guid: string): Promise<QueryItem> => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/organizations/roles_by_guid/${role_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка запроса на роль')
        }

        const data: ApiResponseRoleGuid = await response.json()
        // Извлекаем первый элемент массива, т.к. он всегда содержит единственный объект
        return data.result[0]
    } catch (error) {
        console.error('Ошибка:', error)
        // Возвращаем объект по умолчанию, соответствующий типу QueryItem
        return {
            guid: '',
            name: '',
            organization_guid: '',
            created_at: '',
            updated_at: '',
        }
    }
}

export const getContractSupplierRowKey = (
    supplier: Partial<ContractSupplier>
): string => {
    return String(
        supplier.guid ??
            supplier.supplier_guid ??
            supplier.supplier_inn ??
            supplier.temp_id ??
            ''
    )
}

export const isSameContractSupplier = (
    supplier: Partial<ContractSupplier>,
    rowKey: string
): boolean => {
    return getContractSupplierRowKey(supplier) === rowKey
}

const ContractEditPage: React.FC = () => {
    const loadedContract = useLoaderData() as Contract

    const [contract, setContract] = useState<Contract>(loadedContract)
    const [contractPositions, setContractPositions] = useState<
        ContractPosition[]
    >([])
    const [contractPositionsLoading, setContractPositionsLoading] =
        useState(false)

    const [contractSuppliers, setContractSuppliers] = useState<
        ContractSupplier[]
    >([])
    const [contractSuppliersLoading, setContractSuppliersLoading] =
        useState(false)

    const [orgData, setOrgData] = useState<OrganizationInfo>()

    const { user } = useAuth()
    const { theme } = useContext(ThemeContext)

    const { contract_reg_number: contract_reg_number } = useParams()
    const { contract_guid: contract_guid } = useParams()
    const { contract_action: contract_action } = useParams()

    const [isChangingCurrency, setChangingCurrency] = useState(false)

    const [isUserRoleName, setUserRoleName] = useState('')
    const [structList, setStructList] = useState<Structure[]>([])
    const [currencyList, setCurrencyList] = useState<CurrencyList[]>([])
    const [currencyRate, setCurrencyRate] = useState<CurrencyRate>()
    const [oksmList, setOksmList] = useState<CountryList[]>([])
    const [regionRfList, setRegionRfList] = useState<RegionRf[]>([])

    const [tab, setTab] = useState<string>('Договор')

    const [isUserStructGuid, setUserStructGuid] = useState('')
    const [isUserStructName, setUserStructName] = useState('')

    const [contractsLoading, setContractsLoading] = useState(false)
    const [contractsError, setContractsError] = useState<string | null>(null)

    const [errors, setErrors] = useState<ContractEditErrors>({})
    const [errorPopup, setErrorPopup] = useState(false)
    const [isHaveError, setHaveError] = useState<boolean>(true)

    const [haveContractDocuments, setHaveContractDocuments] =
        useState<boolean>(false)

    const [planPositionPopupOpen, setPlanPositionPopupOpen] = useState(false)
    const [replacePlanPositionPopupOpen, setReplacePlanPositionPopupOpen] =
        useState(false)

    const [pendingSelectedPlanPosition, setPendingSelectedPlanPosition] =
        useState<{
            plan: {
                guid: string
                registration_number: number
                plan_type: string | null
            }
            position: {
                guid: string
                ordinal_number: number
                contract_subject: string | null
                plan_guid: string
                organization_guid: string
                type_object_purchase_position: string | null
                currency_code: string | null
                currency_digital_code: number | null
                currency_name: string | null
                is_electronic: boolean | null
                is_purchase_smb: boolean | null
                purchase_method_name: string | null
                maximum_contract_price: number | string | null
                exchange_rate: number | null
                exchange_rate_date: string | null
                maximum_contract_price_rub: number | null
            }
        } | null>(null)

    const [purchasePopupOpen, setPurchasePopupOpen] = useState(false)
    const [replacePurchasePopupOpen, setReplacePurchasePopupOpen] =
        useState(false)

    const [pendingSelectedPurchase, setPendingSelectedPurchase] = useState<{
        purchase: Purchase
        purchase_lot: PurchaseLot
        purchase_method: PurchaseMethod
    } | null>(null)

    // сбрасываем кеш при смене договора
    useEffect(() => {
        setContract(loadedContract ?? null)
        // тут же можно сбрасывать связанные состояния
    }, [loadedContract?.guid])

    const loadPurchaseLotItems = async (
        purchaseGuid: string,
        lotGuid: string
    ): Promise<PurchaseLotItem[]> => {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases/lots/items?purchase_guid=${purchaseGuid}&lot_guid=${lotGuid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка загрузки позиций лота')
        }

        const data = await response.json()
        return Array.isArray(data) ? data : []
    }

    const parseExchangeRate = (value: unknown): number | null => {
        if (value === null || value === undefined || value === '') return null

        const n = Number(String(value).replace(',', '.'))
        return Number.isNaN(n) ? null : n
    }

    const loadOrganizationData = async (): Promise<OrganizationInfo> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    const data = await response.json()
                    setOrgData(data)
                    return data
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getCurrencyList = async (): Promise<CurrencyList[]> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/currency',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на курсы валют')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getOksmList = async (): Promise<CountryList[]> => {
        return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/oksm', {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на Страны')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getRegionRfList = async (): Promise<RegionRf[]> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/region_rf',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на регионы закупок')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const loadContractPositions = async (
        contractGuid: string
    ): Promise<ContractPosition[]> => {
        setContractPositionsLoading(true)

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    '/api/backend/v1/contract_position/contract_guid/' +
                    contractGuid,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error('Failed to fetch contract positions')
            }

            const data = await response.json()
            setContractPositions(data)
            return data
        } catch (e) {
            console.error(e)
            return []
        } finally {
            setContractPositionsLoading(false)
        }
    }

    const loadContracSuppliers = async (
        contractGuid: string
    ): Promise<ContractSupplier[]> => {
        setContractSuppliersLoading(true)

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    '/api/backend/v1/contract_supplier_info/contract_guid/' +
                    contractGuid,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error('Failed to fetch contract suppliers')
            }

            const data = await response.json()
            setContractSuppliers(data)
            return data
        } catch (e) {
            console.error(e)
            return []
        } finally {
            setContractSuppliersLoading(false)
        }
    }

    useEffect(() => {
        if (contract?.guid) {
            loadContractPositions(contract.guid)
            loadContracSuppliers(contract.guid)
        }
    }, [contract?.guid])

    useEffect(() => {
        const fetchData = async () => {
            try {
                const organization = await loadOrganizationData()
                if (organization) {
                    setOrgData(organization)
                }

                const role = await getRole(user?.object.role_id)
                setUserRoleName(role.name)

                const structs = await getStructureList()
                if (Array.isArray(structs)) {
                    setStructList(structs)
                    if (structs) {
                        const matchingStruct = structs.find(
                            (struct) =>
                                struct.guid.toLowerCase() ===
                                user?.object.struct_id
                        )

                        const structShortName =
                            matchingStruct?.struct_short_name || ''
                        setUserStructName(structShortName)
                        const structGuid = matchingStruct?.guid || ''
                        setUserStructGuid(structGuid)

                        // Только для создания — проставляем дефолт
                        // if (action === 'create') {
                        //     setContract((prev) => ({
                        //         ...prev,
                        //         struct_guid: prev.struct_guid ?? structGuid,
                        //         struct_name:
                        //             prev.struct_name ?? structShortName,
                        //     }))
                        // }
                    }
                } else {
                    console.error('Нет данных:', structs)
                }

                const currency = await getCurrencyList()
                if (Array.isArray(currency)) {
                    setCurrencyList(currency)
                } else {
                    console.error('Нет данных:', currency)
                }

                const oksm = await getOksmList()
                if (Array.isArray(oksm)) {
                    setOksmList(oksm)
                } else {
                    console.error('Нет данных:', oksm)
                }

                const regionrf = await getRegionRfList()
                if (Array.isArray(regionrf)) {
                    setRegionRfList(regionrf)
                } else {
                    console.error('Нет данных:', regionrf)
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    const isEmptyRequired = (value: unknown): boolean => {
        return value === null || value === undefined || value === ''
    }

    const hasCountryOfOrigin = (
        countryOfOrigin: ContractPosition['country_of_origin']
    ): boolean => {
        return (
            !!countryOfOrigin &&
            Array.isArray(countryOfOrigin.country) &&
            countryOfOrigin.country.length > 0 &&
            countryOfOrigin.country.some(
                (country) =>
                    !isEmptyRequired(country.name) &&
                    !isEmptyRequired(country.digital_code)
            )
        )
    }

    const hasCountryManufacturer = (
        countryManufacturer: ContractPosition['country_manufacturer']
    ): boolean => {
        return (
            !!countryManufacturer &&
            !isEmptyRequired(countryManufacturer.name) &&
            !isEmptyRequired(countryManufacturer.digital_code)
        )
    }

    const getPositionLabel = (
        position: ContractPosition,
        index: number
    ): string => {
        return `Позиция №${position.ordinal_number || index + 1}`
    }

    const validateContractPositions = (
        positions: ContractPosition[]
    ): ContractPositionsInfoError => {
        const result: ContractPositionsInfoError = {}

        if (positions.length === 0) {
            result.have_position =
                'Информация о закупаемых товарах, работах, услугах обязательна'
            return result
        }

        const positionErrors: Record<number, ContractPositionRowError> = {}

        positions.forEach((position, index) => {
            const rowError: ContractPositionRowError = {}
            const label = getPositionLabel(position, index)

            if (isEmptyRequired(position.contract_position_name)) {
                rowError.contract_position_name = `${label}: "Наименование товаров, работ, услуг" - Поле обязательное к заполнению`
            }

            if (isEmptyRequired(position.type_object_purchase)) {
                rowError.type_object_purchase = `${label}: "Тип объекта закупки" - Поле обязательное к заполнению`
            }

            if (isEmptyRequired(position.okpd2_code)) {
                rowError.okpd2_code = `${label}: "Код ОКПД2" - Поле обязательное к заполнению`
            }

            if (isEmptyRequired(position.okpd2_name)) {
                rowError.okpd2_name = `${label}: "Наименование ОКПД2" - Поле обязательное к заполнению`
            }

            if (!position.impossible_to_determine_attr) {
                if (isEmptyRequired(position.qty)) {
                    rowError.qty = `${label}: "Количество (объем)" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(position.okei_code)) {
                    rowError.okei_code = `${label}: "Код единицы измерения" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(position.okei_name)) {
                    rowError.okei_name = `${label}: "Наименование единицы измерения" - Поле обязательное к заполнению`
                }
            }

            if (isEmptyRequired(position.unit_price)) {
                rowError.unit_price = `${label}: "Цена за единицу" - Поле обязательное к заполнению`
            }

            if (isEmptyRequired(position.currency_name)) {
                rowError.currency_name = `${label}: "Наименование валюты" - Поле обязательное к заполнению`
            }

            if (isEmptyRequired(position.currency_digital_code)) {
                rowError.currency_digital_code = `${label}: "Код валюты" - Поле обязательное к заполнению`
            }

            if (
                !isEmptyRequired(position.currency_digital_code) &&
                Number(position.currency_digital_code) !== 643
            ) {
                if (isEmptyRequired(position.exchange_rate)) {
                    rowError.exchange_rate = `${label}: "Курс валюты" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(position.rub_unit_price)) {
                    rowError.rub_unit_price = `${label}: "Цена за единицу (руб.)" - Поле обязательное к заполнению`
                }
            }

            // Проверяется только , если это ТОВАР
            if (position.type_object_purchase === 'G') {
                if (!hasCountryOfOrigin(position.country_of_origin)) {
                    rowError.country_of_origin = `${label}: "Страна происхождения товара" - Необходимо указать хотя бы одну страну`
                }

                if (!hasCountryManufacturer(position.country_manufacturer)) {
                    rowError.country_manufacturer = `${label}: "Страна регистрации производителя товара" - Поле обязательное к заполнению`
                }
            }

            if (position.lot_item_national_regime === true) {
                if (isEmptyRequired(position.registry_type)) {
                    rowError.registry_type = `${label}: "Тип реестра" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(position.reg_number)) {
                    rowError.reg_number = `${label}: "Номер реестровой записи" - Поле обязательное к заполнению`
                }

                if (
                    position.registry_type === 'NSI_NP_RRPP' ||
                    position.registry_type === 'NSI_NP_ERPP'
                ) {
                    if (isEmptyRequired(position.score)) {
                        rowError.score = `${label}: "Совокупное количество баллов" - Поле обязательное к заполнению`
                    }
                }
            }

            if (
                position.type_provision ===
                'NATIONAL_FEDERAL_PROJECTS_SUBSIDIES'
            ) {
                if (isEmptyRequired(position.target_code)) {
                    rowError.target_code = `${label}: "Код ЦСР" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(position.type_code)) {
                    rowError.type_code = `${label}: "Код вида расходов (КВР)" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(position.amount_of_budget_funds)) {
                    rowError.amount_of_budget_funds = `${label}: "Объем финансового обеспечения (руб.)" - Поле обязательное к заполнению`
                }
            }

            if (Object.keys(rowError).length > 0) {
                positionErrors[position.ordinal_number || index + 1] = rowError
            }
        })

        if (Object.keys(positionErrors).length > 0) {
            result.positions = positionErrors
        }

        return result
    }

    const getSupplierLabel = (
        supplier: ContractSupplier,
        index: number
    ): string => {
        const supplierName =
            supplier.supplier_short_name ||
            supplier.supplier_name ||
            supplier.supplier_inn ||
            ''

        return supplierName
            ? `Контрагент №${index + 1} (${supplierName})`
            : `Контрагент №${index + 1}`
    }

    const validateContractSuppliers = (
        suppliers: ContractSupplier[]
    ): ContractSuppliersInfoError => {
        const result: ContractSuppliersInfoError = {}

        if (suppliers.length === 0) {
            result.have_supplier =
                'Сведения о поставщиках (исполнителях, подрядчиках) обязательны'
            return result
        }

        const supplierErrors: Record<number, ContractSupplierRowError> = {}

        suppliers.forEach((supplier, index) => {
            const rowError: ContractSupplierRowError = {}
            const label = getSupplierLabel(supplier, index)

            const isLegalEntity = supplier.supplier_type === 'L'
            const isPhysicalPerson = supplier.supplier_type === 'P'
            const isTaxRegistered = supplier.supplier_tax === true
            const isNonResident = supplier.supplier_non_resident === true

            const validateCommonName = () => {
                if (isEmptyRequired(supplier.supplier_name)) {
                    rowError.supplier_name = `${label}: "Полное наименование" - Поле обязательное к заполнению`
                }
            }

            const validateTaxRegisteredCommon = () => {
                if (isEmptyRequired(supplier.supplier_inn)) {
                    rowError.supplier_inn = `${label}: "ИНН" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_registration_date)) {
                    rowError.supplier_registration_date = `${label}: "Дата постановки на учет" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_address_region_name)) {
                    rowError.supplier_address_region = `${label}: "Субъект РФ" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_address_region_code)) {
                    rowError.supplier_address_region = `${label}: "Субъект РФ" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_address_area)) {
                    rowError.supplier_address_area = `${label}: "Муниципальный район, городской округ, внутригородская территория в составе субъекта РФ" - Поле обязательное к заполнению`
                }
            }

            const validateNonResidentCommon = () => {
                if (isEmptyRequired(supplier.supplier_code)) {
                    rowError.supplier_code = `${label}: "Идентификационный код налогоплательщика" - Поле обязательное к заполнению`
                }
            }

            const validateNonResidentTaxRegisteredCommon = () => {
                if (isEmptyRequired(supplier.supplier_code)) {
                    rowError.supplier_code = `${label}: "Идентификационный код налогоплательщика" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_inn)) {
                    rowError.supplier_inn = `${label}: "ИНН" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_kpp)) {
                    rowError.supplier_kpp = `${label}: "КПП" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_registration_date)) {
                    rowError.supplier_registration_date = `${label}: "Дата постановки на учет" - Поле обязательное к заполнению`
                }
            }

            const validateAddressCommon = () => {
                if (isEmptyRequired(supplier.supplier_address_post_code)) {
                    rowError.supplier_address_post_code = `${label}: "Почтовый индекс" - Поле обязательное к заполнению`
                }

                if (
                    isEmptyRequired(supplier.supplier_address_country_name) ||
                    isEmptyRequired(supplier.supplier_address_digital_code)
                ) {
                    rowError.supplier_address_country = `${label}: "Страна" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_address_house)) {
                    rowError.supplier_address_house = `${label}: "Тип и номер здания, сооружения" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_address_phone)) {
                    rowError.supplier_address_phone = `${label}: "Номера телефонов" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_address_email)) {
                    rowError.supplier_address_email = `${label}: "Адреса электронной почты" - Поле обязательное к заполнению`
                }
            }

            if (isTaxRegistered && isNonResident) {
                validateCommonName()
                validateNonResidentTaxRegisteredCommon()
                validateAddressCommon()
            }

            if (isLegalEntity && isNonResident) {
                validateCommonName()
                validateNonResidentCommon()
                validateAddressCommon()
            } else if (isLegalEntity && isTaxRegistered) {
                validateCommonName()

                if (isEmptyRequired(supplier.supplier_kpp)) {
                    rowError.supplier_kpp = `${label}: "КПП" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_okopf)) {
                    rowError.supplier_okopf = `${label}: "ОКОПФ" - Поле обязательное к заполнению`
                }

                if (isEmptyRequired(supplier.supplier_okpo)) {
                    rowError.supplier_okpo = `${label}: "ОКПО" - Поле обязательное к заполнению`
                }

                validateTaxRegisteredCommon()
                validateAddressCommon()
            }

            if (isPhysicalPerson && isNonResident) {
                validateCommonName()
                validateNonResidentCommon()
                validateAddressCommon()
            } else if (isPhysicalPerson && isTaxRegistered) {
                validateCommonName()
                validateTaxRegisteredCommon()
                validateAddressCommon()
            }

            if (supplier.supplier_provider === true) {
                if (
                    isEmptyRequired(supplier.supplier_provider_include_msp_date)
                ) {
                    rowError.supplier_provider_include_msp_date = `${label}: "Дата включения в реестр МСП" - Поле обязательное к заполнению`
                }
            }

            if (Object.keys(rowError).length > 0) {
                supplierErrors[index + 1] = rowError
            }
        })

        if (Object.keys(supplierErrors).length > 0) {
            result.suppliers = supplierErrors
        }

        return result
    }

    // Функция валидации, которая собирает ошибки для всех компонентов
    const validateContractForm = (): boolean => {
        // const plannedYear = getStartDate(pos).getFullYear()
        // const reportingYear = plan[0]?.reporting_year
        // const skipValidation =
        //     (reportingYear &&
        //         plannedYear - reportingYear >= 1 &&
        //         pos.is_purchase_smb === true) ||
        //     pos.planned_after_second_year === true

        const contractChangeInfoError: ContractChangeInfoError = {}
        const contractPurchaseInfoError: ContractPurchaseInfoError = {}
        const contractBaseInfoError: ContractBaseInfoError = {}
        const contractConfirmingDocsError: ContractConfirmingDocsError = {}
        const contractSuppliersInfoError: ContractSuppliersInfoError = {}
        const contractDocumentsError: ContractDocumentsError = {}
        const contractPositionsInfoError: ContractPositionsInfoError = {}

        // Проверяем информацию об изменении, если это изменение

        // По данным ContractChangeInfo
        if (contract.version != 1 && contract.status == 'M') {
            if (
                contract.modification_description == '' ||
                contract.modification_description == null
            ) {
                contractChangeInfoError.modification_description =
                    '"Причина изменения информации о договоре" - Поле обязательное к заполнению'
            }

            if (contract.change_contract) {
                if (
                    contract.approve_date == '' ||
                    contract.approve_date == null
                ) {
                    contractChangeInfoError.approve_date =
                        '"Дата утверждения изменений" - Поле обязательное к заполнению'
                }

                if (contract.has_good_info) {
                    if (contract.contract_change_docs == null) {
                        contractChangeInfoError.contract_change_docs =
                            'Документ об изменении договора обязателен'
                    }
                }
            }
        }

        // По данным ContractPurchaseInfo
        if (contract.notice_44 || contract.notice_not_placed_by_fz223_p5_s4) {
            if (
                contract.plan_position_number == 0 ||
                contract.plan_position_number == null
            ) {
                contractPurchaseInfoError.plan_position_number =
                    '"Позиция плана закупки" - Поле обязательно к заполнению'
            }
        }

        // ПОКА ЧТО ТАК
        // if (contract.resume_date == '' || contract.resume_date == null) {
        //     contractPurchaseInfoError.resume_date =
        //         '"Дата подведения итогов закупки" - Поле обязательно к заполнению'
        // }

        if (contract.customer_appealed_or_needs_approval) {
            if (
                contract.customer_approval_or_antimonopoly_descision_date ==
                    '' ||
                contract.customer_approval_or_antimonopoly_descision_date ==
                    null
            ) {
                contractPurchaseInfoError.customer_approval_or_antimonopoly_descision_date =
                    '"Дата одобрения органом управления заказчика или вынесения решения антимонопольного органа" - Поле обязательно к заполнению'
            }
        }

        // По данным ContractBaseInfo
        if (contract.contract_date == '' || contract.contract_date == null) {
            contractBaseInfoError.contract_date =
                '"Дата заключения договора" - Поле обязательно к заполнению'
        }

        if (
            contract.subject_contract == '' ||
            contract.subject_contract == null
        ) {
            contractBaseInfoError.subject_contract =
                '"Предмет договора" - Поле обязательно к заполнению'
        }

        if (contract.price == 0 || contract.price == null) {
            contractBaseInfoError.price =
                '"Цена договора" - Поле обязательно к заполнению'
        }

        if (
            contract.start_execution_date == '' ||
            contract.start_execution_date == null
        ) {
            contractBaseInfoError.start_execution_date =
                '"Дата начала исполнения" - Поле обязательно к заполнению'
        }

        if (
            contract.end_execution_date == '' ||
            contract.end_execution_date == null
        ) {
            contractBaseInfoError.end_execution_date =
                '"Дата окончания исполнения" - Поле обязательно к заполнению'
        }

        if (contract.has_subcontractor) {
            if (
                contract.subcontractors_total == 0 ||
                contract.subcontractors_total == null
            ) {
                contractBaseInfoError.subcontractors_total =
                    '"Общая стоимость договоров с субподрядчиками" - Поле обязательно к заполнению'
            }
        }

        // По данным isContractDocs
        if (contract.contract_confirming_docs == null) {
            contractConfirmingDocsError.contract_confirming_docs =
                'Основание для заключения договора'
        }

        // По данным contractSuppliers
        const contractSuppliersValidationError =
            validateContractSuppliers(contractSuppliers)

        Object.assign(
            contractSuppliersInfoError,
            contractSuppliersValidationError
        )

        // По данным contractDocuments
        if (haveContractDocuments == false) {
            contractDocumentsError.have_documents_items =
                'Документы договора обязательны'
        }

        // По данным contractPosition
        const contractPositionsValidationError =
            validateContractPositions(contractPositions)

        Object.assign(
            contractPositionsInfoError,
            contractPositionsValidationError
        )

        setErrors({
            contractChangeInfo: contractChangeInfoError,
            contractPurchaseInfo: contractPurchaseInfoError,
            contractBaseInfo: contractBaseInfoError,
            contractConfirmingDocs: contractConfirmingDocsError,
            contractSuppliersInfo: contractSuppliersInfoError,
            contractDocuments: contractDocumentsError,
            contractPositionsInfo: contractPositionsInfoError,
        })

        // Если роль admin или specialist, учитываем оба блока, иначе только purchaseContract
        const isContractChangeInfoValid =
            Object.keys(contractChangeInfoError).length === 0
        const isContractPurchaseInfoValid =
            Object.keys(contractPurchaseInfoError).length === 0
        const isContractBaseInfoValid =
            Object.keys(contractBaseInfoError).length === 0
        const isContractConfirmingDocsValid =
            Object.keys(contractConfirmingDocsError).length === 0
        const isContractSuppliersInfoValid =
            Object.keys(contractSuppliersInfoError).length === 0
        const isContractDocumentsValid =
            Object.keys(contractDocumentsError).length === 0
        const isContractPositionsInfoValid =
            Object.keys(contractPositionsInfoError).length === 0

        setErrorPopup(true)
        console.log(errors)

        return (
            isContractChangeInfoValid &&
            isContractPurchaseInfoValid &&
            isContractBaseInfoValid &&
            isContractConfirmingDocsValid &&
            isContractSuppliersInfoValid &&
            isContractDocumentsValid &&
            isContractPositionsInfoValid
        )
    }

    const handleValidation = () => {
        if (validateContractForm()) {
            // Если форма валидна, можно выполнять дальнейшие действия (например, отправку данных)
            setHaveError(false)
            console.log('Форма валидна')
        } else {
            setHaveError(true)
            console.log('Найдены ошибки в форме')
        }
    }

    const handleErrorPopup = () => {
        setErrorPopup(!errorPopup)
    }

    // Рекурсивная функция для обхода объекта ошибок и сбора строковых сообщений
    const flattenErrors = (obj: any): string[] => {
        let errorList: string[] = []
        Object.keys(obj).forEach((key) => {
            const value = obj[key]
            if (typeof value === 'string') {
                errorList.push(value)
            } else if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (typeof item === 'string') {
                        errorList.push(item)
                    } else if (typeof item === 'object' && item !== null) {
                        errorList = errorList.concat(flattenErrors(item))
                    }
                })
            } else if (typeof value === 'object' && value !== null) {
                errorList = errorList.concat(flattenErrors(value))
            }
        })
        return errorList
    }

    // Функция для получения строки ошибок в нужном порядке, с изменённым именем переменной
    const getErrorsText = (errorObj: ContractEditErrors): string => {
        // Задаём порядок вывода ошибок
        const order: (keyof ContractEditErrors)[] = [
            'contractChangeInfo',
            'contractBaseInfo',
            'contractPurchaseInfo',
            'contractConfirmingDocs',
            'contractSuppliersInfo',
            'contractDocuments',
            'contractPositionsInfo',
        ]

        let errorList: string[] = []
        order.forEach((key) => {
            if (errorObj[key]) {
                errorList = errorList.concat(flattenErrors(errorObj[key]))
            }
        })
        // Объединяем каждое сообщение с новой строки
        return errorList.join('\n\n')
    }

    const getCurrencyRateByDate = async (
        num_code: string,
        currency_date: string
    ): Promise<CurrencyRate> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/currency_rate/by_code_and_date?num_code=${num_code}&currency_date=${currency_date}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    console.log('Отсутствует курс валюты на конкретную дату')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const parseCurrencyCbr = async (
        date: string
    ): Promise<{ message: string } | null> => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/currency_rate/fetch_from_cbr?date=${date}`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (response.ok) {
                return await response.json()
            } else {
                console.error('Ошибка при запросе парсинга курсов ЦБ')
                return null
            }
        } catch (e) {
            console.error('Ошибка сети при парсинге курсов ЦБ:', e)
            return null
        }
    }

    // Хелперы для обновления позиции
    const toNullableNumber = (value: unknown): number | null => {
        if (value === '' || value === null || value === undefined) return null
        const n = Number(String(value).replace(',', '.'))
        return Number.isNaN(n) ? null : n
    }

    const calcRubUnitPrice = (
        unitPrice: number | null | undefined,
        currencyDigitalCode: number | null | undefined,
        exchangeRate: number | null | undefined
    ): number | null => {
        const price = toNullableNumber(unitPrice)
        if (price === null) return null

        if (currencyDigitalCode === 643) {
            return null
        }

        const rate = toNullableNumber(exchangeRate)
        if (rate === null) return null

        return Number((price * rate).toFixed(2))
    }

    const handleCurrencyExchangeRateChange = (newExchangeRate: number | 0) => {
        setContract((prev) => {
            const isRub = newExchangeRate <= 0
            const rate = isRub ? 1 : newExchangeRate
            const digitalCode = isRub ? 643 : prev.currency_digital_code
            const currencyCode = isRub ? 'RUB' : prev.currency_code
            const currencyName = isRub ? 'Российский рубль' : prev.currency_name
            const exchangeRate = isRub ? null : rate
            const exchangeRateDate = isRub ? null : prev.exchange_rate_date

            const convertItems = (items: ItemDetail[]) =>
                items.map((item) => ({
                    ...item,
                    summ_rub: isRub
                        ? undefined
                        : Math.round(item.summ * rate * 100) / 100,
                }))

            const updateLongTermVolumes = (
                volume: LongTermContractVolumes | null
            ): LongTermContractVolumes | null => {
                if (!volume) return null

                const newItems = convertItems(volume.details?.items ?? [])
                const newVolumeRub = isRub
                    ? undefined
                    : Math.round(volume.volume * rate * 100) / 100

                return {
                    ...volume,
                    details: { items: newItems },
                    volume_rub: newVolumeRub,
                }
            }

            const rub_price = isRub
                ? Number(prev.price)
                : roundTo(prev.price * rate, 2)

            return {
                ...prev,
                currency_digital_code: digitalCode,
                currency_code: currencyCode,
                currency_name: currencyName,
                exchange_rate: exchangeRate,
                exchange_rate_date: exchangeRateDate,
                rub_price: rub_price,
                long_term_volumes: updateLongTermVolumes(
                    prev.long_term_contract_volumes
                ),
            }
        })

        setContractPositions((prev) =>
            prev.map((item) => {
                const isRub = newExchangeRate <= 0
                const rate = isRub ? null : newExchangeRate

                return {
                    ...item,
                    exchange_rate: rate,
                    rub_unit_price: calcRubUnitPrice(
                        item.unit_price,
                        item.currency_digital_code,
                        rate
                    ),
                }
            })
        )

        setHaveError(true)
    }

    const onCurrencyUpdate = async (
        contractData: Contract,
        currencyCode: number | null,
        date: string
    ): Promise<Partial<Contract>> => {
        setChangingCurrency(true)

        const newPos: Partial<Contract> = {
            currency_digital_code: currencyCode,
        }

        let currency_code: string
        let currency_name: string
        let exchange_rate: number | null
        let exchange_rate_date: string | null

        if (currencyCode === 643) {
            // ручная установка RUB
            currency_code = 'RUB'
            currency_name = 'Российский рубль'
            exchange_rate = null
            exchange_rate_date = null
        } else {
            let currencyRateByDate = await getCurrencyRateByDate(
                String(currencyCode),
                date
            )

            if (!currencyRateByDate) {
                const parseResult = await parseCurrencyCbr(date)

                if (parseResult && parseResult.message) {
                    const match = parseResult.message.match(
                        /Курсы на (\d{4}-\d{2}-\d{2})/
                    )
                    const parsedDateFromMessage = match?.[1]

                    if (
                        parsedDateFromMessage &&
                        (parseResult.message.includes('успешно') ||
                            parseResult.message.includes('уже загружены'))
                    ) {
                        currencyRateByDate = await getCurrencyRateByDate(
                            String(currencyCode),
                            parsedDateFromMessage
                        )
                    }
                }

                // Если всё равно нет данных — ставим курс 1
                if (!currencyRateByDate) {
                    currencyRateByDate = {
                        guid: '',
                        value_id: '',
                        nominal: 1,
                        currency_date: date,
                        num_code: String(currencyCode),
                        char_code: contractData.currency_code ?? '',
                        name: contractData.currency_name ?? '',
                        value: 1,
                        vunit_rate: 1,
                        char_sign: null,
                    }
                }
            }

            setCurrencyRate(currencyRateByDate)

            currency_code = currencyRateByDate.char_code
            currency_name = String(currencyRateByDate.name)
            exchange_rate = currencyRateByDate.vunit_rate
            exchange_rate_date = currencyRateByDate.currency_date
        }

        newPos.currency_code = currency_code
        newPos.currency_name = currency_name
        newPos.exchange_rate = exchange_rate

        if (contractData.exchange_rate == null) {
            handleCurrencyExchangeRateChange(exchange_rate ? exchange_rate : 0)
        }

        const recalculate = (
            items: { summ: number }[]
        ): { items: any[]; volume: number; volume_rub?: number } => {
            const updated = items.map((item) => ({
                ...item,
                summ_rub:
                    newPos.exchange_rate === null
                        ? undefined
                        : item.summ * Number(exchange_rate),
            }))
            const volume = updated.reduce((acc, item) => acc + item.summ, 0)
            const volume_rub =
                exchange_rate === null
                    ? undefined
                    : updated.reduce(
                          (acc, item) => acc + (item.summ_rub ?? 0),
                          0
                      )
            return { items: updated, volume, volume_rub }
        }

        if (contract.long_term_contract_volumes) {
            const { items, volume, volume_rub } = recalculate(
                contract.long_term_contract_volumes.details?.items ?? []
            )
            newPos.long_term_contract_volumes = {
                details: { items },
                volume,
                volume_rub,
            }
        }

        return newPos
    }

    function getUpdatedLongTermVolumes(
        existingVolumes: LongTermContractVolumes | null,
        yearsInRange: number[],
        contractData: Contract
    ): LongTermContractVolumes {
        if (existingVolumes === null) {
            return {
                volume: 0,
                details: {
                    items: yearsInRange.map((year) => ({
                        summ: 0,
                        year,
                        ...(contractData.currency_digital_code !== 643
                            ? { summ_rub: null }
                            : {}),
                    })),
                },
                volume_rub:
                    contractData.currency_digital_code !== 643 ? 0 : undefined,
                volume_price_prepaid_stage: null,
                volume_price_prepaid_stage_rub:
                    contractData.currency_digital_code !== 643
                        ? null
                        : undefined,
            }
        }

        const existingItemsSource = existingVolumes.details?.items ?? []

        const existingYears = existingItemsSource.map((item) => item.year)

        const newItems: ItemDetail[] = yearsInRange
            .filter((year) => !existingYears.includes(year))
            .map((year) => ({
                summ: 0,
                year,
                ...(contractData.currency_digital_code !== 643
                    ? { summ_rub: 0 }
                    : {}),
            }))

        const existingItems = existingItemsSource.filter((item) =>
            yearsInRange.includes(item.year)
        )

        const allItems = [...newItems, ...existingItems].sort(
            (a, b) => a.year - b.year
        )

        const volume = allItems.reduce(
            (acc, item) => Math.round((acc + (item.summ ?? 0)) * 100) / 100,
            0
        )

        const volumeRub =
            contractData.currency_digital_code !== 643
                ? allItems.reduce(
                      (acc, item) =>
                          Math.round((acc + (item.summ_rub ?? 0)) * 100) / 100,
                      0
                  )
                : undefined

        return {
            ...existingVolumes,
            details: {
                items: allItems,
            },
            volume,
            volume_rub: volumeRub,
        }
    }

    function onDateUpdate(
        contractData: Contract,
        isStart: boolean,
        date: string | null
    ): Contract {
        const getYearsInRange = (start: Date, end: Date) => {
            const startYear = start.getFullYear()
            const endYear = end.getFullYear()

            return Array.from(
                { length: endYear - startYear + 1 },
                (_, i) => startYear + i
            )
        }

        const startValue = isStart ? date : contractData.start_execution_date
        const endValue = !isStart ? date : contractData.end_execution_date

        const startDate = new Date(startValue ?? '')
        const endDate = new Date(endValue ?? '')

        const isStartValid = !Number.isNaN(startDate.getTime())
        const isEndValid = !Number.isNaN(endDate.getTime())

        let newLongTermVolumes = contractData.long_term_contract_volumes

        if (!isStartValid || !isEndValid) {
            newLongTermVolumes = null
        } else {
            const shouldClearLongTerm =
                endDate.getFullYear() - startDate.getFullYear() <= 0

            if (shouldClearLongTerm) {
                newLongTermVolumes = null
            } else {
                const yearsInRange = getYearsInRange(startDate, endDate)

                newLongTermVolumes = getUpdatedLongTermVolumes(
                    contractData.long_term_contract_volumes,
                    yearsInRange,
                    contractData
                )
            }
        }

        return {
            ...contractData,
            start_execution_date: startValue,
            end_execution_date: endValue,
            long_term_contract_volumes: newLongTermVolumes,
        }
    }

    // обновление одной позиции по guid
    const updateContractPosition = (
        ordinalNumber: number,
        patch: Partial<ContractPosition>
    ) => {
        setContractPositions((prev) =>
            prev.map((item) => {
                if (item.ordinal_number !== ordinalNumber) return item

                const next: ContractPosition = {
                    ...item,
                    ...patch,
                }

                next.rub_unit_price = calcRubUnitPrice(
                    next.unit_price,
                    next.currency_digital_code,
                    next.exchange_rate
                )

                if (next.impossible_to_determine_attr) {
                    next.qty = null
                    next.okei_code = null
                    next.okei_name = null
                }

                if (!next.lot_item_national_regime) {
                    next.registry_type = null
                    next.reg_number = null
                    next.score = null
                }

                if (
                    next.registry_type !== 'NSI_NP_RRPP' &&
                    next.registry_type !== 'NSI_NP_ERPP'
                ) {
                    next.score = null
                }

                return next
            })
        )
    }

    // удаление одной позиции
    const deleteContractPosition = (ordinalNumber: number) => {
        setContractPositions((prev) =>
            prev
                .filter((item) => item.ordinal_number !== ordinalNumber)
                .map((item, index) => ({
                    ...item,
                    ordinal_number: index + 1,
                }))
        )
    }

    // Массовое удаление
    const deleteContractPositionsByOrdinalNumbers = (
        ordinalNumbers: number[]
    ) => {
        setContractPositions((prev) =>
            prev
                .filter((item) => !ordinalNumbers.includes(item.ordinal_number))
                .map((item, index) => ({
                    ...item,
                    ordinal_number: index + 1,
                }))
        )
    }

    //Массоваое копирование
    const copyContractPositionsByOrdinalNumbers = (
        ordinalNumbers: number[]
    ) => {
        setContractPositions((prev) => {
            const selected = prev.filter((item) =>
                ordinalNumbers.includes(item.ordinal_number)
            )

            if (!selected.length) return prev

            let nextOrdinal = getNextOrdinalNumber(prev)

            const copiedItems = selected.map((item) => {
                const copied: ContractPosition = {
                    ...item,
                    guid: null,
                    eis_guid: null,
                    ordinal_number: nextOrdinal,
                }

                nextOrdinal += 1
                return copied
            })

            return [...prev, ...copiedItems]
        })
    }

    // helper поиска валюты
    const getCurrencyMeta = (
        currencyListData: CurrencyList[],
        digitalCode: number
    ): {
        currency_code: string
        currency_digital_code: number
        currency_name: string
    } | null => {
        const found = currencyListData.find(
            (item) => Number(item.num_code) === Number(digitalCode)
        )

        if (!found) return null

        return {
            currency_code: found.char_code,
            currency_digital_code: Number(found.num_code),
            currency_name: found.name,
        }
    }

    // применить валюту ко всем позициям
    const applyCurrencyToPositions = (
        positions: ContractPosition[],
        currencyMeta: {
            currency_code: string
            currency_digital_code: number
            currency_name: string
        },
        exchangeRate: number | null
    ): ContractPosition[] => {
        return positions.map((item) => {
            const next: ContractPosition = {
                ...item,
                currency_code: currencyMeta.currency_code,
                currency_digital_code: currencyMeta.currency_digital_code,
                currency_name: currencyMeta.currency_name,
                exchange_rate:
                    currencyMeta.currency_digital_code === 643
                        ? null
                        : exchangeRate,
            }

            next.rub_unit_price = calcRubUnitPrice(
                next.unit_price,
                next.currency_digital_code,
                next.exchange_rate
            )

            return next
        })
    }

    // смена валюты
    const handleContractCurrencyChange = async (
        newCurrencyDigitalCode: number
    ) => {
        const updated = await onCurrencyUpdate(
            contract,
            newCurrencyDigitalCode,
            contract.contract_date ?? getCurrentDate()
        )

        const currencyMeta = getCurrencyMeta(
            currencyList,
            newCurrencyDigitalCode
        )

        if (!currencyMeta) {
            setChangingCurrency(false)
            return
        }

        const nextExchangeRate =
            newCurrencyDigitalCode === 643
                ? null
                : (updated.exchange_rate ?? contract.exchange_rate ?? 1)

        setContract((prev) => ({
            ...prev,
            ...updated,
            currency_code: currencyMeta.currency_code,
            currency_digital_code: currencyMeta.currency_digital_code,
            currency_name: currencyMeta.currency_name,
            exchange_rate: nextExchangeRate,
            exchange_rate_date:
                newCurrencyDigitalCode === 643
                    ? null
                    : (updated.exchange_rate_date ?? prev.exchange_rate_date),
            rub_price:
                currencyMeta.currency_digital_code === 643
                    ? Number(prev.price)
                    : Math.round(
                          Number(prev.price) *
                              Number(nextExchangeRate ?? 1) *
                              100
                      ) / 100,
        }))

        setContractPositions((prev) =>
            applyCurrencyToPositions(prev, currencyMeta, nextExchangeRate)
        )
        setChangingCurrency(false)
    }

    // обновление одной позиции по guid
    const updateContractSupplier = (
        rowKey: string,
        patch: Partial<ContractSupplier>
    ) => {
        setContractSuppliers((prev) =>
            prev.map((item) => {
                if (!isSameContractSupplier(item, rowKey)) return item

                return {
                    ...item,
                    ...patch,
                }
            })
        )

        setContract((prev) => ({
            ...prev,
            suppliers:
                prev.suppliers?.map((supplier) => {
                    if (!isSameContractSupplier(supplier, rowKey))
                        return supplier

                    return {
                        ...supplier,
                        ...patch,
                        guid: supplier.guid,
                    }
                }) ?? [],
        }))
    }

    // удаление одной позиции
    const deleteContractSupplier = (rowKey: string) => {
        setContract((prev) => ({
            ...prev,
            suppliers:
                prev.suppliers?.filter(
                    (supplier) => !isSameContractSupplier(supplier, rowKey)
                ) ?? [],
        }))

        setContractSuppliers((prev) =>
            prev.filter((supplier) => !isSameContractSupplier(supplier, rowKey))
        )
    }

    const addEmptyContractPosition = () => {
        if (!contract?.guid) return

        setContractPositions((prev) => {
            const ordinal = getNextOrdinalNumber(prev)
            const newItem = createEmptyContractPosition(contract.guid, ordinal)
            return [...prev, newItem]
        })
    }

    const addContractPositionsFromPlanRows = (rows: PlanPositionRow[]) => {
        if (!contract?.guid || !rows.length) return

        setContractPositions((prev) => {
            let nextOrdinal = getNextOrdinalNumber(prev)

            const mapped = rows.map((row) => {
                const item = createContractPositionFromPlanRow(
                    row,
                    contract.guid,
                    nextOrdinal,
                    null,
                    null,
                    null,
                    null,
                    null
                )
                nextOrdinal += 1
                return item
            })

            return [...prev, ...mapped]
        })
    }

    const addContractPositionsFromContractRows = (rows: ContractPosition[]) => {
        if (!contract?.guid || !rows.length) return

        setContractPositions((prev) => {
            let nextOrdinal = getNextOrdinalNumber(prev)

            const mapped = rows.map((row) => {
                const item = createContractPositionFromContract(
                    row,
                    [...prev],
                    contract.guid,
                    nextOrdinal
                )
                nextOrdinal += 1
                return item
            })

            return [...prev, ...mapped]
        })
    }

    const getPlanYearFromContract = (
        contract_data: Contract
    ): number | null => {
        const sourceDate =
            contract_data.contract_date || contract_data.sys_created_at
        if (!sourceDate) return null

        const date = new Date(sourceDate)
        if (Number.isNaN(date.getTime())) return null

        return date.getFullYear()
    }

    const loadPlanPositionRows = async (
        regNumber: number,
        posGuid: string
    ): Promise<PlanPositionRow[]> => {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v2/plan_position_rows/?reg_number=${regNumber}&pos_guid=${posGuid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка загрузки строк позиции плана')
        }

        const data = await response.json()
        return Array.isArray(data) ? data : []
    }

    const getPurchaseTypeInfoByPlanPosition = (
        purchaseMethodName: string | null | undefined,
        isPurchaseSmb: boolean | null | undefined
    ): {
        purchase_type_info_name: string
        purchase_type_info_code: string
        digital_purchase_code: string
    } => {
        const methodName = (purchaseMethodName ?? '').toLowerCase()
        const smbSuffix = isPurchaseSmb ? '1' : '2'

        if (methodName.includes('единств')) {
            return {
                purchase_type_info_name:
                    'Закупка у единственного поставщика (подрядчика, исполнителя)',
                purchase_type_info_code: '30000',
                digital_purchase_code: `30000${smbSuffix}`,
            }
        }

        if (methodName.includes('конкурс')) {
            return {
                purchase_type_info_name: 'Конкурс',
                purchase_type_info_code: '11011',
                digital_purchase_code: `11011${smbSuffix}`,
            }
        }

        if (methodName.includes('аукцион')) {
            return {
                purchase_type_info_name: 'Аукцион',
                purchase_type_info_code: '12012',
                digital_purchase_code: `12012${smbSuffix}`,
            }
        }

        return {
            purchase_type_info_name:
                'Иной способ закупки, предусмотренный правовым актом заказчика, указанным в части 1 статьи 2 Федерального закона',
            purchase_type_info_code: '40000',
            digital_purchase_code: `40000${smbSuffix}`,
        }
    }

    const applySelectedPlanPosition = async (selected: {
        plan: {
            guid: string
            registration_number: number
            plan_type: string | null
        }
        position: {
            guid: string
            ordinal_number: number
            contract_subject: string | null
            plan_guid: string
            organization_guid: string
            type_object_purchase_position: string | null
            currency_code: string | null
            currency_digital_code: number | null
            currency_name: string | null
            is_electronic: boolean | null
            is_purchase_smb: boolean | null
            purchase_method_name: string | null
            maximum_contract_price: number | string | null
            exchange_rate: number | null
            exchange_rate_date: string | null
            maximum_contract_price_rub: number | null
        }
    }) => {
        if (!contract?.guid) return

        try {
            const rows = await loadPlanPositionRows(
                selected.plan.registration_number,
                selected.position.guid
            )

            const maximumContractPrice =
                selected.position.maximum_contract_price === null ||
                selected.position.maximum_contract_price === undefined ||
                selected.position.maximum_contract_price === ''
                    ? null
                    : Number(selected.position.maximum_contract_price)

            const mappedRows = rows.map((row, index) =>
                createContractPositionFromPlanRow(
                    row,
                    contract.guid,
                    index + 1,
                    rows.length === 1 ? maximumContractPrice : null,
                    selected.position.currency_code || null,
                    selected.position.currency_digital_code || null,
                    selected.position.currency_name || null,
                    selected.position.exchange_rate || null
                )
            )

            const purchaseTypeInfo = getPurchaseTypeInfoByPlanPosition(
                selected.position.purchase_method_name,
                selected.position.is_purchase_smb
            )

            const currencyDigitalCode = selected.position.currency_digital_code

            setContract((prev) => ({
                ...prev,

                plan_registration_number: String(
                    selected.plan.registration_number
                ),
                plan_guid: selected.plan.guid,
                plan_position_number: selected.position.ordinal_number,
                plan_position_type: selected.plan.plan_type,
                plan_position_guid: selected.position.guid,
                plan_position_contract_subject:
                    selected.position.contract_subject,

                currency_code: selected.position.currency_code ?? 'RUB',
                currency_digital_code:
                    selected.position.currency_digital_code ?? 643,
                currency_name:
                    selected.position.currency_name ?? 'Российский рубль',

                subject_contract: selected.position.contract_subject ?? null,
                digital_purchase: selected.position.is_electronic ?? false,

                provider: selected.position.is_purchase_smb === true,
                provider_code:
                    selected.position.is_purchase_smb === true ? '30' : null,

                purchase_type_info_name:
                    purchaseTypeInfo.purchase_type_info_name,
                purchase_type_info_code:
                    purchaseTypeInfo.purchase_type_info_code,
                digital_purchase_code: purchaseTypeInfo.digital_purchase_code,

                price: selected.position.purchase_method_name
                    ?.toLowerCase()
                    .includes('единств')
                    ? (maximumContractPrice ?? prev.price)
                    : prev.price,

                exchange_rate:
                    currencyDigitalCode !== 643
                        ? selected.position.exchange_rate
                        : null,
                exchange_rate_date:
                    currencyDigitalCode !== 643
                        ? selected.position.exchange_rate_date
                        : null,
                rub_price:
                    currencyDigitalCode !== 643
                        ? selected.position.maximum_contract_price_rub
                        : null,
            }))

            setContractPositions(mappedRows)
        } catch (e) {
            console.error(e)
        } finally {
            setPendingSelectedPlanPosition(null)
            setPlanPositionPopupOpen(false)
            setReplacePlanPositionPopupOpen(false)
        }
    }

    const applySelectedPurchaseLot = async (selected: {
        purchase: Purchase
        purchase_lot: PurchaseLot
        purchase_method: PurchaseMethod
    }) => {
        if (!contract?.guid) return

        try {
            const lot = selected.purchase_lot
            const purchase = selected.purchase
            const purchaseMethod = selected.purchase_method

            const lotItems = await loadPurchaseLotItems(purchase.guid, lot.guid)

            let planRows: PlanPositionRow[] = []

            if (lot.plan_registration_number && lot.plan_pos_guid) {
                try {
                    planRows = await loadPlanPositionRows(
                        Number(lot.plan_registration_number),
                        lot.plan_pos_guid
                    )
                } catch (e) {
                    console.error(e)
                    planRows = []
                }
            }

            const currencyDigitalCode =
                lot.currency?.digital_code === null ||
                lot.currency?.digital_code === undefined
                    ? null
                    : Number(lot.currency.digital_code)

            const exchangeRate =
                currencyDigitalCode !== 643
                    ? parseExchangeRate(lot.exchange_info?.exchange_rate)
                    : null

            const maximumContractPrice =
                lot.initial_sum === null || lot.initial_sum === undefined
                    ? null
                    : Number(lot.initial_sum)

            const mappedRows = lotItems.map((lotItem, index) =>
                createContractPositionFromPurchaseLotItem(
                    lotItem,
                    planRows.length > 0 ? (planRows[index] ?? null) : null,
                    contract.guid,
                    index + 1,
                    lot.currency?.code ?? null,
                    currencyDigitalCode,
                    lot.currency?.name ?? null,
                    exchangeRate,
                    lotItems.length === 1 ? maximumContractPrice : null
                )
            )

            const purchaseTypeInfo = getPurchaseTypeInfoByPlanPosition(
                purchaseMethod.purchase_method_name ??
                    purchase.purchase_method_name ??
                    null,
                purchaseMethod.purchase_smb_type
            )

            setContract((prev) => ({
                ...prev,

                purchase_notice_guid: purchase.guid,
                purchase_notice_number:
                    purchase.registration_number === null ||
                    purchase.registration_number === undefined
                        ? null
                        : String(purchase.registration_number),
                purchase_publication_date_time:
                    purchase.publication_date_time ?? null,
                purchase_notice_name: purchase.name ?? null,

                lot_guid: lot.guid,

                currency_code: lot.currency?.code ?? 'RUB',
                currency_digital_code: currencyDigitalCode ?? 643,
                currency_name: lot.currency?.name ?? 'Российский рубль',

                subject_contract: lot.subject ?? null,

                digital_purchase: purchaseMethod.is_electronic ?? false,

                provider: purchaseMethod.purchase_smb_type === true,
                provider_code:
                    purchaseMethod.purchase_smb_type === true ? '30' : null,

                purchase_type_info_name:
                    purchaseTypeInfo.purchase_type_info_name,
                purchase_type_info_code:
                    purchaseTypeInfo.purchase_type_info_code,
                digital_purchase_code: purchaseTypeInfo.digital_purchase_code,

                emergency: purchase.emergency ?? false,

                price: purchaseMethod.purchase_method_name
                    ?.toLowerCase()
                    .includes('единств')
                    ? (maximumContractPrice ?? prev.price)
                    : prev.price,
            }))

            setContractPositions(mappedRows)
        } catch (e) {
            console.error(e)
        } finally {
            setPendingSelectedPurchase(null)
            setPurchasePopupOpen(false)
            setReplacePurchasePopupOpen(false)
        }
    }

    const handleSelectPurchaseLot = async (selected: {
        purchase: Purchase
        purchase_lot: PurchaseLot
        purchase_method: PurchaseMethod
    }) => {
        if (!contractPositions.length) {
            await applySelectedPurchaseLot(selected)
            return
        }

        setPendingSelectedPurchase(selected)
        setReplacePurchasePopupOpen(true)
    }

    const handleSelectPlanPosition = async (selected: {
        plan: {
            guid: string
            registration_number: number
            plan_type: string | null
        }
        position: {
            guid: string
            ordinal_number: number
            contract_subject: string | null
            plan_guid: string
            organization_guid: string
            type_object_purchase_position: string | null
            currency_code: string | null
            currency_digital_code: number | null
            currency_name: string | null
            is_electronic: boolean | null
            is_purchase_smb: boolean | null
            purchase_method_name: string | null
            maximum_contract_price: number | string | null
            exchange_rate: number | null
            exchange_rate_date: string | null
            maximum_contract_price_rub: number | null
        }
    }) => {
        if (!contractPositions.length) {
            await applySelectedPlanPosition(selected)
            return
        }

        setPendingSelectedPlanPosition(selected)
        setReplacePlanPositionPopupOpen(true)
    }

    const addContractSupplier = (supplier: ContractSupplier) => {
        const newSupplier: ContractSupplier = {
            guid: null,
            supplier_guid: supplier.guid ?? null,

            supplier_name: supplier.supplier_name ?? null,
            supplier_short_name: supplier.supplier_short_name ?? null,
            supplier_brand_name: supplier.supplier_brand_name ?? null,
            supplier_additional_full_name:
                supplier.supplier_additional_full_name ?? null,
            supplier_inn: supplier.supplier_inn ?? null,
            supplier_kpp: supplier.supplier_kpp ?? null,
            supplier_okpo: supplier.supplier_okpo ?? null,
            supplier_code: supplier.supplier_code ?? null,
            supplier_additional_code: supplier.supplier_additional_code ?? null,
            supplier_additional_info: supplier.supplier_additional_info ?? null,
            supplier_type: supplier.supplier_type ?? null,
            supplier_provider: supplier.supplier_provider ?? null,
            supplier_provider_code: supplier.supplier_provider_code ?? null,
            supplier_subcontractor: supplier.supplier_subcontractor ?? null,
            supplier_subcontractor_code:
                supplier.supplier_subcontractor_code ?? null,
            supplier_individual: supplier.supplier_individual ?? null,
            supplier_non_resident: supplier.supplier_non_resident ?? null,
            supplier_registration_date:
                supplier.supplier_registration_date ?? null,
            supplier_tax: supplier.supplier_tax ?? null,
            supplier_okopf: supplier.supplier_okopf ?? null,
            supplier_okopf_name: supplier.supplier_okopf_name ?? null,
            supplier_address_egrul_egrip_changed:
                supplier.supplier_address_egrul_egrip_changed ?? null,
            supplier_address_country_name:
                supplier.supplier_address_country_name ?? null,
            supplier_address_digital_code:
                supplier.supplier_address_digital_code ?? null,
            supplier_address_post_code:
                supplier.supplier_address_post_code ?? null,
            supplier_address_oktmo: supplier.supplier_address_oktmo ?? null,
            supplier_address_oktmo_name:
                supplier.supplier_address_oktmo_name ?? null,
            supplier_address_region_code:
                supplier.supplier_address_region_code ?? null,
            supplier_address_region_name:
                supplier.supplier_address_region_name ?? null,
            supplier_address_area: supplier.supplier_address_area ?? null,
            supplier_address_city: supplier.supplier_address_city ?? null,
            supplier_address_settlement:
                supplier.supplier_address_settlement ?? null,
            supplier_address_street: supplier.supplier_address_street ?? null,
            supplier_address_email: supplier.supplier_address_email ?? null,
            supplier_address_phone: supplier.supplier_address_phone ?? null,
            supplier_address_office: supplier.supplier_address_office ?? null,
            supplier_address_building:
                supplier.supplier_address_building ?? null,
            supplier_address_house: supplier.supplier_address_house ?? null,
            supplier_address_planning_structure:
                supplier.supplier_address_planning_structure ?? null,
            supplier_address_admin_division:
                supplier.supplier_address_admin_division ?? null,
            supplier_provider_include_msp_date:
                supplier.supplier_provider_include_msp_date ?? null,
            supplier_is_in_order_173n:
                supplier.supplier_is_in_order_173n ?? null,
            supplier_self_employed: supplier.supplier_self_employed ?? null,
            annotation: supplier.annotation ?? null,

            organization_guid: contract.organization_guid ?? null,
            contract_guid: contract.guid ?? null,

            sys_created_at: null,
            sys_updated_at: null,

            temp_id: supplier.temp_id ?? crypto.randomUUID(),
        }

        setContractSuppliers((prev) => {
            const exists = prev.some(
                (item) =>
                    (!!item.supplier_guid &&
                        !!newSupplier.supplier_guid &&
                        item.supplier_guid === newSupplier.supplier_guid) ||
                    (!!item.supplier_inn &&
                        !!newSupplier.supplier_inn &&
                        item.supplier_inn === newSupplier.supplier_inn)
            )

            if (exists) return prev
            return [...prev, newSupplier]
        })

        setContract((prev) => {
            const exists =
                prev.suppliers?.some(
                    (item) =>
                        (!!item.guid &&
                            !!newSupplier.supplier_guid &&
                            item.guid === newSupplier.supplier_guid) ||
                        (!!item.supplier_inn &&
                            !!newSupplier.supplier_inn &&
                            item.supplier_inn === newSupplier.supplier_inn)
                ) ?? false

            if (exists) return prev

            return {
                ...prev,
                suppliers: [
                    ...(prev.suppliers ?? []),
                    {
                        guid: newSupplier.supplier_guid,
                        supplier_name: newSupplier.supplier_name,
                        supplier_short_name: newSupplier.supplier_short_name,
                        supplier_inn: newSupplier.supplier_inn,
                        supplier_address_country_name:
                            newSupplier.supplier_address_country_name,
                        supplier_address_post_code:
                            newSupplier.supplier_address_post_code,
                        supplier_address_region_name:
                            newSupplier.supplier_address_region_name,
                        supplier_address_city:
                            newSupplier.supplier_address_city,
                        supplier_address_street:
                            newSupplier.supplier_address_street,
                        supplier_address_house:
                            newSupplier.supplier_address_house,
                        supplier_address_building:
                            newSupplier.supplier_address_building,
                        supplier_address_office:
                            newSupplier.supplier_address_office,
                        temp_id: newSupplier.temp_id,
                    },
                ],
            }
        })
    }

    useEffect(() => {
        handleContractCurrencyChange(contract.currency_digital_code ?? 643)
    }, [contract.contract_date])

    return (
        <div className=" flex flex-col w-full h-screen items-start justify-start ">
            <div className="flex flex-col w-full h-fit items-center justify-center">
                <div className="flex flex-col w-full h-[90px] items-center justify-center bg-[#e5e5e5] dark:bg-[#000000]">
                    <ContractStage contract={contract} theme={theme} />
                </div>
                <div className="flex flex-col w-full min-h-[100px] items-center justify-center bg-[#ebebeb] dark:bg-[#111111]">
                    <ContractInfo
                        contract={contract}
                        theme={theme}
                        currencyList={currencyList}
                    />
                </div>
            </div>

            <div className="grow flex flex-row w-full h-[calc(100vh_-_250px)] items-center justify-center gap-5 px-5 py-5 bg-[#f0f3f5] dark:bg-[#181818] ">
                <div className="flex flex-col min-w-[1270px] max-w-[1270px] h-full min-h-0 gap-y-12 items-start justify-start overflow-x-visible overflow-y-auto custom-scrollbar">
                    <fieldset
                        className={`flex flex-col w-full gap-10`}
                        disabled={false}
                    >
                        {contract.version > 1 && (
                            <ContractChangeInfo
                                contractAction={
                                    contract_action == 'edit' ||
                                    contract.contract_stage == 'Отправка в ЕИС'
                                        ? true
                                        : false
                                }
                                contractGuid={contract?.guid}
                                theme={theme}
                                contractsLoading={contractsLoading}
                                contractsError={contractsError}
                                currencyList={currencyList}
                                contractRubPrice={contract?.rub_price}
                                contractPrice={contract?.price}
                                contract={contract}
                                onChangeContract={(chnage_contract) => {
                                    setContract((prev) => ({
                                        ...prev,
                                        change_contract: chnage_contract,
                                    }))
                                }}
                                onModificationDescription={(
                                    modification_description
                                ) => {
                                    setContract((prev) => ({
                                        ...prev,
                                        modification_description:
                                            modification_description,
                                    }))
                                }}
                                onApproveDate={(approve_date) => {
                                    setContract((prev) => ({
                                        ...prev,
                                        approve_date: approve_date,
                                    }))
                                }}
                                onHasGoodInfo={(has_good_info) => {
                                    setContract((prev) => ({
                                        ...prev,
                                        has_good_info: has_good_info,
                                    }))
                                }}
                                contractChangeDocs={
                                    contract.contract_change_docs
                                }
                                onChangeDocs={(change_docs) => {
                                    setContract((prev) => ({
                                        ...prev,
                                        contract_change_docs: change_docs,
                                    }))
                                }}
                                errors={errors.contractChangeInfo}
                            />
                        )}

                        <ContractPurchaseInfo
                            contractAction={
                                contract_action == 'edit' ||
                                contract.contract_stage == 'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contract?.guid}
                            theme={theme}
                            contractsLoading={contractsLoading}
                            contractsError={contractsError}
                            currencyList={currencyList}
                            contractRubPrice={contract?.rub_price}
                            contractPrice={contract?.price}
                            contract={contract}
                            onNotice44={(notice_44) => {
                                setContract((prev) => ({
                                    ...prev,
                                    notice_44: notice_44,
                                }))
                            }}
                            onNotice44Num={(notice_44_num) => {
                                setContract((prev) => ({
                                    ...prev,
                                    notice_44_num: notice_44_num,
                                }))
                            }}
                            onNotice44Lot={(lot_44_num) => {
                                setContract((prev) => ({
                                    ...prev,
                                    lot_44_num: lot_44_num,
                                }))
                            }}
                            onNoticeNotPlacedByFz223p5s4={(
                                notice_not_placed_by_fz223_p5_s4
                            ) => {
                                setContract((prev) => ({
                                    ...prev,
                                    notice_not_placed_by_fz223_p5_s4:
                                        notice_not_placed_by_fz223_p5_s4,
                                }))
                            }}
                            onEmergency={(emergency) => {
                                setContract((prev) => ({
                                    ...prev,
                                    emergency: emergency,
                                }))
                            }}
                            onResumeDate={(resume_date) => {
                                setContract((prev) => ({
                                    ...prev,
                                    resume_date: resume_date,
                                }))
                                setHaveError(true)
                            }}
                            onPurchaseInfo={(purchase_info) => {
                                setContract((prev) => ({
                                    ...prev,
                                    purchase_notice_guid:
                                        purchase_info.purchase_notice_guid,
                                    purchase_notice_number:
                                        purchase_info.purchase_notice_number,
                                    purchase_publication_date_time:
                                        purchase_info.purchase_publication_date_time,
                                    purchase_notice_name:
                                        purchase_info.purchase_notice_name,
                                    lot_guid: purchase_info.lot_guid,
                                }))
                            }}
                            onPlanInfo={(plan_info) => {
                                setContract((prev) => ({
                                    ...prev,
                                    plan_registration_number:
                                        plan_info.plan_registration_number,
                                    plan_guid: plan_info.plan_guid,
                                    plan_position_number:
                                        plan_info.plan_position_number,
                                    plan_position_type:
                                        plan_info.plan_position_type,
                                    plan_position_guid:
                                        plan_info.plan_position_guid,
                                    plan_position_contract_subject:
                                        plan_info.plan_position_contract_subject,
                                }))
                            }}
                            onPurchaseTypeInfo={(purchase_type_info) => {
                                setContract((prev) => ({
                                    ...prev,
                                    purchase_type_info_code:
                                        purchase_type_info.purchase_type_info_code,
                                    purchase_type_info_name:
                                        purchase_type_info.purchase_type_info_name,
                                }))
                            }}
                            onDigitalPurchase={(digital_purchase) => {
                                setContract((prev) => ({
                                    ...prev,
                                    digital_purchase: digital_purchase,
                                }))
                            }}
                            onProvider={(provider) => {
                                setContract((prev) => ({
                                    ...prev,
                                    provider: provider,
                                }))
                            }}
                            onTermination={(termination) => {
                                setContract((prev) => ({
                                    ...prev,
                                    termination: termination,
                                }))
                            }}
                            onExtension={(extension) => {
                                setContract((prev) => ({
                                    ...prev,
                                    extension: extension,
                                }))
                            }}
                            onCustomerAppealedOrNeedsApproval={(
                                customer_appealed_or_needs_approval
                            ) => {
                                setContract((prev) => ({
                                    ...prev,
                                    customer_appealed_or_needs_approval:
                                        customer_appealed_or_needs_approval,
                                }))
                                if (!customer_appealed_or_needs_approval) {
                                    setContract((prev) => ({
                                        ...prev,
                                        customer_approval_or_antimonopoly_descision_date:
                                            null,
                                    }))
                                }
                            }}
                            onCustomerApprovalOrAntimonopolyDescisionDate={(
                                customer_approval_or_antimonopoly_descision_date
                            ) => {
                                setContract((prev) => ({
                                    ...prev,
                                    customer_approval_or_antimonopoly_descision_date:
                                        customer_approval_or_antimonopoly_descision_date,
                                }))
                                setHaveError(true)
                            }}
                            errors={errors.contractPurchaseInfo}
                            onOpenPlanPositionPopup={() =>
                                setPlanPositionPopupOpen(true)
                            }
                            onOpenPurchasePopup={() =>
                                setPurchasePopupOpen(true)
                            }
                        />

                        <ContractBaseInfo
                            contractAction={
                                contract_action == 'edit' ||
                                contract.contract_stage == 'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contract?.guid}
                            theme={theme}
                            contractsLoading={contractsLoading}
                            contractsError={contractsError}
                            currencyData={currencyList}
                            contractRubPrice={contract?.rub_price}
                            contractPrice={contract?.price}
                            contract={contract}
                            onContractDate={(contract_date) => {
                                setContract((prev) => ({
                                    ...prev,
                                    contract_date: contract_date,
                                }))
                                setContract((prev) =>
                                    onDateUpdate(
                                        prev,
                                        true /* isStart */,
                                        contract_date
                                    )
                                )
                            }}
                            onNumber={(contract_name) => {
                                setContract((prev) => ({
                                    ...prev,
                                    contract_name: contract_name,
                                }))
                            }}
                            onSubject={(subject_contract) => {
                                setContract((prev) => ({
                                    ...prev,
                                    subject_contract: subject_contract,
                                }))
                            }}
                            currency={contract.currency_digital_code}
                            onCurrencyChange={handleContractCurrencyChange}
                            currencyExchangeRate={contract.exchange_rate ?? 1}
                            onCurrencyExchangeRateChange={
                                handleCurrencyExchangeRateChange
                            }
                            price={contract.price ? Number(contract.price) : 0}
                            onPrice={(price) => {
                                setContract((prev) => ({
                                    ...prev,
                                    price,
                                    rub_price:
                                        prev.currency_digital_code === 643
                                            ? null
                                            : Math.round(
                                                  Number(price ?? 0) *
                                                      Number(
                                                          prev.exchange_rate ??
                                                              1
                                                      ) *
                                                      100
                                              ) / 100,
                                }))
                            }}
                            onStartExecutionDate={(start_execution_date) => {
                                setContract((prev) =>
                                    onDateUpdate(
                                        prev,
                                        true /* isStart */,
                                        start_execution_date
                                    )
                                )
                            }}
                            onEndExecutionDate={(end_execution_date) => {
                                setContract((prev) =>
                                    onDateUpdate(
                                        prev,
                                        false /* isStart */,
                                        end_execution_date
                                    )
                                )
                            }}
                            onAdditionalInfo={(additional_info) => {
                                setContract((prev) => ({
                                    ...prev,
                                    additional_info: additional_info,
                                }))
                            }}
                            onSubcontractor={(has_subcontractor) => {
                                if (has_subcontractor) {
                                    setContract((prev) => ({
                                        ...prev,
                                        has_subcontractor: has_subcontractor,
                                        has_subcontractor_code: '30',
                                    }))
                                } else {
                                    setContract((prev) => ({
                                        ...prev,
                                        has_subcontractor: has_subcontractor,
                                        has_subcontractor_code: null,
                                        subcontractors_total: null,
                                    }))
                                }
                            }}
                            onSubcontractorTotal={(subcontractors_total) => {
                                setContract((prev) => ({
                                    ...prev,
                                    subcontractors_total: subcontractors_total,
                                }))
                            }}
                            isChangingCurrency={isChangingCurrency}
                            errors={errors.contractBaseInfo}
                        />

                        {contract.long_term_contract_volumes?.details
                            ?.items && (
                            <ContractPaymentBreakdown
                                title="Информация об объемах оплаты долгосрочного договора"
                                contractAction={
                                    contract_action == 'edit' ||
                                    contract.contract_stage == 'Отправка в ЕИС'
                                        ? true
                                        : false
                                }
                                yearlyPayments={
                                    contract.long_term_contract_volumes
                                }
                                onYearlyPaymentsChange={(year, value) => {
                                    const normalizedValue = toNumber(value) // Заменяем пустое значение на 0
                                    if (contract.long_term_contract_volumes) {
                                        const item = (
                                            contract.long_term_contract_volumes
                                                .details?.items ?? []
                                        ).find((y) => y.year === year)
                                        if (item) {
                                            setContract((prev) => {
                                                if (
                                                    prev.long_term_contract_volumes ===
                                                    null
                                                ) {
                                                    return prev
                                                }

                                                // Округляем newVolume, чтобы избежать ошибок с плавающей точкой

                                                const currentSumm = toNumber(
                                                    item.summ
                                                )

                                                const newVolume =
                                                    Math.round(
                                                        (prev
                                                            .long_term_contract_volumes
                                                            .volume -
                                                            currentSumm +
                                                            normalizedValue) *
                                                            100
                                                    ) / 100

                                                const updatedItems = (
                                                    prev
                                                        .long_term_contract_volumes
                                                        .details?.items ?? []
                                                ).map((dt) => {
                                                    if (dt.year === year) {
                                                        // Округляем summRub для предотвращения ошибок при пересчёте суммы в рубли
                                                        const summRub =
                                                            prev.currency_digital_code !==
                                                            643
                                                                ? Math.round(
                                                                      normalizedValue *
                                                                          (prev.exchange_rate ||
                                                                              1) *
                                                                          100
                                                                  ) / 100
                                                                : undefined

                                                        return {
                                                            ...dt,
                                                            summ: normalizedValue,
                                                            ...(summRub !==
                                                                undefined && {
                                                                summ_rub:
                                                                    summRub,
                                                            }),
                                                        }
                                                    }

                                                    return dt
                                                })

                                                // Округляем volumeRub для точности отображения итоговой суммы в рублях
                                                const volumeRub =
                                                    prev.currency_digital_code !==
                                                    643
                                                        ? Math.round(
                                                              newVolume *
                                                                  (prev.exchange_rate ||
                                                                      1) *
                                                                  100
                                                          ) / 100
                                                        : undefined

                                                const currency =
                                                    contract.currency_digital_code !=
                                                    643
                                                        ? {
                                                              code: contract.currency_code,
                                                              name: contract.currency_name,
                                                              digital_code:
                                                                  contract.currency_digital_code,
                                                          }
                                                        : {
                                                              code: 'RUB',
                                                              name: 'Российский рубль',
                                                              digital_code: 643,
                                                          }

                                                const exchange_rate =
                                                    contract.currency_digital_code !=
                                                    643
                                                        ? contract.exchange_rate
                                                        : null

                                                return {
                                                    ...prev,
                                                    long_term_contract_volumes:
                                                        {
                                                            ...prev.long_term_contract_volumes,
                                                            volume: newVolume,
                                                            volume_rub:
                                                                volumeRub,
                                                            details: {
                                                                items: updatedItems,
                                                            },
                                                            currency: currency,
                                                            exchange_rate:
                                                                exchange_rate,
                                                        },
                                                }
                                            })
                                        }
                                    }
                                }}
                                currency={contract.currency_digital_code}
                                currencyExchangeRate={
                                    contract.exchange_rate ?? undefined
                                }
                                currencyList={currencyList}
                                errors={errors.contractPurchaseInfo} // передаём ошибки, относящиеся к PurchaseContractInfo
                            />
                        )}

                        <ContractConfirmingDocs
                            theme={theme}
                            contractAction={
                                contract_action == 'edit' ||
                                contract.contract_stage == 'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contract?.guid}
                            contract={contract}
                            errors={errors.contractConfirmingDocs}
                            contractConfirmDocs={
                                contract.contract_confirming_docs
                            }
                            onConfirmDocs={(confirm_docs) => {
                                setContract((prev) => ({
                                    ...prev,
                                    contract_confirming_docs: confirm_docs,
                                }))
                            }}
                        />

                        <ContractSuppliers
                            theme={theme}
                            contractAction={
                                contract_action == 'edit' ||
                                contract.contract_stage == 'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contract?.guid}
                            contract={contract}
                            errors={errors.contractSuppliersInfo}
                            contractSuppliers={contractSuppliers}
                            onUpdateSupplier={updateContractSupplier}
                            onDeleteSupplier={deleteContractSupplier}
                            onAddSupplier={addContractSupplier}
                            oksmList={oksmList}
                            regionRfList={regionRfList}
                        />

                        <ContractDocuments
                            theme={theme}
                            contractAction={
                                contract_action == 'edit' ||
                                contract.contract_stage == 'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contract?.guid}
                            contract={contract}
                            role={isUserRoleName}
                            onContractDocuments={(have_contract_documents) =>
                                setHaveContractDocuments(
                                    have_contract_documents
                                )
                            }
                            errors={errors.contractDocuments}
                            // onAnyEdit={() => setHaveError(true)}
                        />
                        <ContractPositionInfo
                            theme={theme}
                            contractAction={
                                contract_action == 'edit' ||
                                contract.contract_stage == 'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contract?.guid}
                            contract={contract}
                            errors={errors.contractPositionsInfo}
                            currencyList={currencyList}
                            contractPositions={contractPositions}
                            isLoadingContractPosition={contractPositionsLoading}
                            onUpdatePosition={updateContractPosition}
                            onDeletePosition={deleteContractPosition}
                            onDeleteSelected={
                                deleteContractPositionsByOrdinalNumbers
                            }
                            onCopySelected={
                                copyContractPositionsByOrdinalNumbers
                            }
                            onAddEmptyPosition={addEmptyContractPosition}
                            onAddFromPlanRows={addContractPositionsFromPlanRows}
                            onAddFromContractRows={
                                addContractPositionsFromContractRows
                            }
                            orgData={orgData}
                        />
                    </fieldset>
                </div>
            </div>

            <Footer
                theme={theme}
                contractAction={contract_action == 'edit' ? true : false}
                contractGuid={contract_guid}
                contract={contract}
                user_guid={user?.object.user_id}
                contract_user_guid={contract.user_guid}
                contract_supervisor_user_guid={contract.supervisor_user_guid}
                role={isUserRoleName}
                stage={contract.contract_stage}
                status={contract.contract_status}
                handleValidation={handleValidation}
                isHaveError={isHaveError}
                onContractStatus={(contract_status) => {
                    setContract((prev) => ({
                        ...prev,
                        contract_status: contract_status,
                    }))
                }}
                onContractStage={(contract_stage) => {
                    setContract((prev) => ({
                        ...prev,
                        contract_stage: contract_stage,
                    }))
                }}
                contractSuppliers={contractSuppliers}
                contractPosition={contractPositions}
                year={getPlanYearFromContract(contract)}
            />

            <ContractPlanPositionSelectPopup
                open={planPositionPopupOpen}
                theme={theme}
                year={getPlanYearFromContract(contract)}
                onClose={() => {
                    setPlanPositionPopupOpen(false)
                    setPendingSelectedPlanPosition(null)
                }}
                onSelect={handleSelectPlanPosition}
                currency_data={currencyList}
            />

            <ContractPurchaseSelectPopup
                open={purchasePopupOpen}
                theme={theme}
                year={getPlanYearFromContract(contract)}
                currency_data={currencyList}
                onClose={() => {
                    setPurchasePopupOpen(false)
                    setPendingSelectedPurchase(null)
                }}
                onSelect={handleSelectPurchaseLot}
            />

            {replacePurchasePopupOpen && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[500px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Хотите выбрать новое извещение и лот?
                                    <br />
                                    При выборе нового лота все ранее введенные
                                    позиции договора будут стерты.
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={() => {
                                        setReplacePurchasePopupOpen(false)
                                        setPurchasePopupOpen(false)
                                        setPendingSelectedPurchase(null)
                                    }}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={() => {
                                        if (pendingSelectedPurchase) {
                                            applySelectedPurchaseLot(
                                                pendingSelectedPurchase
                                            )
                                        }
                                    }}
                                >
                                    Выбрать
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {replacePlanPositionPopupOpen && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[500px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Хотите выбрать новую позицию плана?
                                    <br />
                                    При выборе новой позиции плана все ранее
                                    введенные позиции договора будут стерты.
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={() => {
                                        setReplacePlanPositionPopupOpen(false)
                                        setPlanPositionPopupOpen(false)
                                        setPendingSelectedPlanPosition(null)
                                    }}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={() => {
                                        if (pendingSelectedPlanPosition) {
                                            applySelectedPlanPosition(
                                                pendingSelectedPlanPosition
                                            )
                                        }
                                    }}
                                >
                                    Выбрать
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {errorPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center z-50"
                    onClick={handleErrorPopup}
                >
                    <div
                        className={`p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] ${
                            !isHaveError ? 'w-[500px]' : 'w-[650px]'
                        }  h-fit`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-stsrt gap-2">
                            {!isHaveError ? (
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="times-circle"
                                >
                                    <path
                                        fill="#187A33"
                                        d="M14.72,8.79l-4.29,4.3L8.78,11.44a1,1,0,1,0-1.41,1.41l2.35,2.36a1,1,0,0,0,.71.29,1,1,0,0,0,.7-.29l5-5a1,1,0,0,0,0-1.42A1,1,0,0,0,14.72,8.79ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                                    ></path>
                                </svg>
                            ) : (
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="times-circle"
                                >
                                    <path
                                        fill="#C15C5C"
                                        d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                    ></path>
                                </svg>
                            )}

                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                {!isHaveError
                                    ? 'Проверка прошла успешно'
                                    : 'Найдены ошибки'}
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            {!isHaveError ? (
                                <div className="flex flex-col w-full space-y-1">
                                    <div>
                                        <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                            Ошибок нет. Для дальнейшей работы
                                            нажмите на кнопку «Готово».
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col w-full space-y-1 gap-2">
                                    <div className="flex flex-col mt-2 space-y-1">
                                        <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                            Внимание! Для дальнейшей работы
                                            необходимо исправить ошибки в
                                            следующих полях:
                                        </label>
                                    </div>
                                    <div className="flex flex-col mt-2 space-y-1">
                                        <textarea
                                            className="form-textarea flex w-full h-full text-[11px] items-start justify-start p-2 rounded-md border-0"
                                            value={getErrorsText(errors)}
                                            rows={21}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleErrorPopup}
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ContractEditPage
